
import Vue from 'vue';
import type { PropOptions } from 'vue';
import { mapGetters, mapState } from 'vuex';
import StarIcon from './assets/star.vue.svg';
import RocketIcon from './assets/rocket.vue.svg';
import AwardIcon from './assets/award.vue.svg';
import BoltIcon from './assets/bolt-alt.vue.svg';
import type { PublicPortfolioConfiguration } from '@/types/graphql';
import ChevronIcon from '~/components/partials/public-portfolio/assets/chevron.vue.svg';
import PublicPortfolioAward from '~/components/partials/public-portfolio/PublicPortfolioAwards/PublicPortfolioAward.vue';
import PublicPortfolioAwardsModal from '~/components/partials/public-portfolio/PublicPortfolioAwards/PublicPortfolioAwardsModal.vue';
import { sortByFieldDesc } from '~/components/utils/sorting_func';
import { capitalize } from '~/components/utils/core/string';
import type { SectionMode } from '~/components/partials/public-portfolio/types';

const importSwiper = () => import('swiper');

const SCROLL_PADDING = 16;
const AWARD_ICONS: Array<string> = ['StarIcon', 'RocketIcon', 'AwardIcon', 'BoltIcon'];

type Award = {
  id: number;
  year: number | string;
  name: string;
  icon: string;
};

export default Vue.extend({
  name: 'PublicPortfolioAwardsSection',
  components: {
    PublicPortfolioAwardsModal,
    PublicPortfolioAward,
    ChevronIcon,
    StarIcon,
    RocketIcon,
    AwardIcon,
    BoltIcon,
  },
  props: {
    mode: {
      type: String,
      default: 'view',
    } as PropOptions<SectionMode>,
    isHeadSection: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    configuration: {
      type: Object,
      required: true,
    } as PropOptions<PublicPortfolioConfiguration>,
  },
  data() {
    return {
      unwatch: undefined as (() => void) | undefined,
    };
  },
  computed: {
    ...mapState('portfolio', ['awards']),
    ...mapGetters('portfolio', ['hasAwards']),
    awardsList(): Array<Award> {
      let awardsList;
      if (!this.hasAwards) {
        awardsList = [
          { id: -1, year: '2021', name: 'Top Rep Q3 Award', icon: 'StarIcon' },
          { id: -2, year: '2021', name: "President's Club", icon: 'RocketIcon' },
          { id: -3, year: '2020', name: 'Top Rep Q2 Award', icon: 'AwardIcon' },
          { id: -4, year: '2019', name: 'Top Rep Q1 Award', icon: 'BoltIcon' },
        ];
      } else {
        awardsList = (this.awards as Array<Award>)
          .map(award => {
            let icon = `${capitalize(award.icon)}Icon`;
            icon = AWARD_ICONS.includes(icon) ? icon : AWARD_ICONS[0];
            return { ...award, icon };
          })
          .sort(sortByFieldDesc('year'));
      }
      return awardsList;
    },
    rootClass(): Record<string, boolean> {
      return {
        'public-portfolio-awards-section': true,
        'public-portfolio-awards-section--empty': !this.hasAwards,
        [`public-portfolio-awards-section--${this.mode}`]: true,
      };
    },
    awardsContainerClass(): Record<string, boolean> {
      return {
        swiper: true,
        'public-portfolio-awards-section__awards-container': true,
        'public-portfolio-awards-section__awards-container--dark-fade': this.isHeadSection,
      };
    },
  },
  mounted() {
    importSwiper().then(({ Swiper, Navigation, Mousewheel }) => {
      Swiper.use([Navigation, Mousewheel]);
      const swiperInstance = new Swiper('.public-portfolio-awards-section__awards-container', {
        slidesPerView: 'auto',
        spaceBetween: SCROLL_PADDING,
        grabCursor: true,
        mousewheel: {
          forceToAxis: true,
        },
        navigation: {
          nextEl: '.public-portfolio-awards-section__next-button',
          prevEl: '.public-portfolio-awards-section__prev-button',
        },
        breakpoints: {
          992: {
            slidesPerView: 4,
          },
        },
      });

      // @ts-ignore
      this.unwatch = this.$store.watch(
        store => store.portfolio.awards,
        (newValue, oldValue) => {
          if (newValue.length !== oldValue.length) {
            swiperInstance.update();
          }
        },
      );
    });
  },
  beforeDestroy() {
    this.unwatch?.();
  },
});
